import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatMoney, STATUS_LABEL } from "@/lib/api";
import {
  renderBrandedHeader,
  renderBrandedFooter,
  brandedTableHeadStyles,
  drawSignatureBlock,
} from "@/lib/pdfBranding";

const COLORS = {
  text:    [26, 28, 26],
  muted:   [110, 115, 110],
  border:  [209, 209, 203],
  primary: [27, 67, 50],
  accent:  [184, 134, 11],
};

const MARGIN = 48;

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
    return new Date(iso).toLocaleDateString("es-CO", { dateStyle: "medium" });
  } catch { return iso; }
}

function genderLabel(g) {
  return g === "F" ? "Femenino" : g === "M" ? "Masculino" : g === "O" ? "Otro" : "—";
}

/**
 * Genera un PDF integral con la ficha del paciente:
 *   - Información personal y de contacto
 *   - Antecedentes médicos / alertas
 *   - Anamnesis (si existe)
 *   - Historial de citas
 *   - Evoluciones clínicas (con firmas del odontólogo)
 *   - Plan de tratamiento
 *   - Presupuestos (resumen)
 *
 * Params:
 *   patient   — objeto del paciente
 *   data      — { appointments, balance, ... } (del GET /patients/:id)
 *   evolutions, anamnesis, plan, budgets, periodontograms — listas opcionales
 *   branding  — branding del tenant
 */
export function generatePatientPdf({ patient, data, evolutions = [], anamnesis = null, plan = [], budgets = [], receipts = [], branding }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // ===== Página 1: Header + datos personales =====
  let y = renderBrandedHeader(doc, branding, {
    title: "HISTORIA CLÍNICA",
    subtitle: `Ficha del paciente · ${new Date().toLocaleDateString("es-CO")}`,
  });
  y += 6;

  // Nombre grande del paciente
  doc.setTextColor(...COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(patient?.name || "—", MARGIN, y);
  y += 22;

  // Tags resumen
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);
  const docLine = patient?.document_number
    ? `${patient.document_type || "CC"} ${patient.document_number}`
    : "Sin documento";
  const headerTags = [
    docLine,
    patient?.eps && `EPS · ${patient.eps}`,
    patient?.regimen,
    patient?.blood_type && `RH ${patient.blood_type}`,
    patient?.pregnant && "Embarazo",
    patient?.smoker && "Fumador(a)",
  ].filter(Boolean);
  doc.text(headerTags.join("  ·  "), MARGIN, y);
  y += 18;

  // Sección: Información personal
  y = renderSectionTitle(doc, "Información personal", y);
  const persFields = [
    ["Teléfono", patient?.phone],
    ["Correo", patient?.email],
    ["Dirección", patient?.address],
    ["Ciudad", patient?.city],
    ["Fecha nacimiento", fmtDate(patient?.birth_date)],
    ["Género", genderLabel(patient?.gender)],
    ["Ocupación", patient?.occupation],
    ["Contacto emergencia", patient?.emergency_contact_name
      ? `${patient.emergency_contact_name}${patient.emergency_contact_phone ? " · " + patient.emergency_contact_phone : ""}`
      : null],
  ];
  y = renderKeyValueGrid(doc, persFields, y);

  // Sección: Antecedentes médicos
  y = renderSectionTitle(doc, "Antecedentes médicos", y + 6);
  const medFields = [
    ["Alergias", patient?.allergies],
    ["Crónicas", patient?.chronic_conditions],
    ["Medicamentos actuales", patient?.current_medications],
    ["Cirugías previas", patient?.previous_surgeries],
    ["Última visita dental", fmtDate(patient?.last_dental_visit)],
    ["Frecuencia cepillado", patient?.brushing_frequency],
  ];
  y = renderKeyValueGrid(doc, medFields, y);

  // Alerta médica destacada si aplica
  const hasAlerts = patient?.allergies || patient?.chronic_conditions || patient?.pregnant || patient?.current_medications;
  if (hasAlerts) {
    if (y > H - 130) { doc.addPage(); y = MARGIN; }
    doc.setFillColor(255, 240, 240);
    doc.setDrawColor(180, 35, 24);
    doc.setLineWidth(1.5);
    doc.rect(MARGIN, y, W - 2 * MARGIN, 50, "FD");
    doc.setTextColor(180, 35, 24);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text("ALERTAS MÉDICAS", MARGIN + 10, y + 16);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(60, 20, 20);
    const alerts = [
      patient.allergies && `Alergias: ${patient.allergies}`,
      patient.chronic_conditions && `Crónicas: ${patient.chronic_conditions}`,
      patient.pregnant && "Embarazo activo",
      patient.current_medications && `Medicamentos: ${patient.current_medications}`,
    ].filter(Boolean).join(" · ");
    const wrapped = doc.splitTextToSize(alerts, W - 2 * MARGIN - 20);
    doc.text(wrapped, MARGIN + 10, y + 30);
    y += 60;
  }

  // Notas
  if (patient?.dental_history || patient?.notes) {
    y = renderSectionTitle(doc, "Notas", y + 4);
    if (patient.dental_history) {
      y = renderLongText(doc, "Historia odontológica", patient.dental_history, y);
    }
    if (patient.notes) {
      y = renderLongText(doc, "Notas clínicas", patient.notes, y + 4);
    }
  }

  // ===== Anamnesis =====
  if (anamnesis && Object.keys(anamnesis).filter((k) => k !== "id" && k !== "patient_id" && k !== "created_at" && k !== "updated_at").length > 0) {
    doc.addPage(); y = MARGIN;
    y = renderSectionTitle(doc, "Anamnesis", y);
    const anamRows = [];
    const ANAM_LABELS = {
      motivo_consulta: "Motivo de consulta",
      enfermedad_actual: "Enfermedad actual",
      antecedentes_familiares: "Antecedentes familiares",
      antecedentes_personales: "Antecedentes personales",
      habitos: "Hábitos",
      medicacion_actual: "Medicación actual",
      alergias_detalle: "Alergias (detalle)",
      embarazo: "Embarazo",
      lactancia: "Lactancia",
      enfermedades_sistemicas: "Enfermedades sistémicas",
      tratamientos_previos: "Tratamientos previos",
      higiene_oral: "Higiene oral",
      observaciones: "Observaciones",
    };
    for (const [k, label] of Object.entries(ANAM_LABELS)) {
      const v = anamnesis[k];
      if (v !== null && v !== undefined && String(v).trim() !== "") {
        anamRows.push([label, typeof v === "boolean" ? (v ? "Sí" : "No") : String(v)]);
      }
    }
    if (anamRows.length > 0) {
      autoTable(doc, {
        startY: y + 4,
        body: anamRows,
        theme: "plain",
        bodyStyles: { fontSize: 9, textColor: COLORS.text, valign: "top" },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 160, textColor: COLORS.muted }, 1: { cellWidth: "auto" } },
        margin: { left: MARGIN, right: MARGIN },
        styles: { cellPadding: { top: 4, bottom: 4, left: 0, right: 0 } },
      });
      y = doc.lastAutoTable.finalY + 12;
    } else {
      doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...COLORS.muted);
      doc.text("Sin información de anamnesis registrada.", MARGIN, y + 8);
      y += 24;
    }
  }

  // ===== Historial de citas =====
  const appts = (data?.appointments || []).slice().sort((a, b) => new Date(a.start) - new Date(b.start));
  if (appts.length > 0) {
    doc.addPage(); y = MARGIN;
    y = renderSectionTitle(doc, `Historial de citas (${appts.length})`, y);
    autoTable(doc, {
      startY: y + 4,
      head: [["Fecha y hora", "Tratamiento", "Estado", "Profesional", "Precio"]],
      body: appts.map((a) => [
        fmtDateTime(a.start),
        a.treatment_name || "—",
        STATUS_LABEL[a.status] || a.status,
        a.doctor?.name || "—",
        formatMoney(a.price || 0),
      ]),
      theme: "striped",
      headStyles: { ...brandedTableHeadStyles(branding), fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLORS.text },
      columnStyles: { 4: { halign: "right", fontStyle: "bold" } },
      margin: { left: MARGIN, right: MARGIN },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // ===== Evoluciones clínicas =====
  if (evolutions.length > 0) {
    doc.addPage(); y = MARGIN;
    y = renderSectionTitle(doc, `Evoluciones clínicas (${evolutions.length})`, y);
    // Las evoluciones llegan ordenadas DESC; las pongo cronológicas
    const evolsAsc = evolutions.slice().sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
    for (const ev of evolsAsc) {
      if (y > H - 160) { doc.addPage(); y = MARGIN; }
      // Banda de fecha + diente
      doc.setFillColor(...COLORS.primary);
      doc.rect(MARGIN, y, W - 2 * MARGIN, 22, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      doc.text(fmtDate(ev.fecha), MARGIN + 8, y + 14);
      if (ev.diente) {
        doc.setFont("helvetica", "normal"); doc.setFontSize(9);
        doc.text(`Diente ${ev.diente}`, MARGIN + 100, y + 14);
      }
      if (ev.doctor?.name) {
        doc.setFont("helvetica", "normal"); doc.setFontSize(9);
        doc.text(ev.doctor.name + (ev.doctor.specialty ? ` · ${ev.doctor.specialty}` : ""), W - MARGIN - 8, y + 14, { align: "right" });
      }
      y += 30;

      doc.setTextColor(...COLORS.text); doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      if (ev.diagnostico) {
        doc.setFont("helvetica", "bold"); doc.text("Diagnóstico:", MARGIN, y);
        doc.setFont("helvetica", "normal");
        const w = doc.splitTextToSize(ev.diagnostico, W - 2 * MARGIN - 80);
        doc.text(w, MARGIN + 80, y);
        y += Math.max(12, w.length * 11);
      }
      doc.setFont("helvetica", "bold"); doc.text("Procedimiento:", MARGIN, y);
      doc.setFont("helvetica", "normal");
      const proc = doc.splitTextToSize(ev.procedimiento || "—", W - 2 * MARGIN - 80);
      doc.text(proc, MARGIN + 80, y);
      y += Math.max(12, proc.length * 11);

      if (ev.observaciones) {
        doc.setFont("helvetica", "bold"); doc.text("Observaciones:", MARGIN, y);
        doc.setFont("helvetica", "normal");
        const obs = doc.splitTextToSize(ev.observaciones, W - 2 * MARGIN - 80);
        doc.text(obs, MARGIN + 80, y);
        y += Math.max(12, obs.length * 11);
      }

      // Firma del doctor si está
      if (ev.doctor?.signature_image) {
        y += 6;
        drawSignatureBlock(doc, {
          x: W - MARGIN - 220,
          baselineY: y + 30,
          width: 220,
          signatureImage: ev.doctor.signature_image,
          label: "Firma del profesional",
          name: ev.doctor.name || "",
          subtitle: ev.doctor.signature_text || ev.doctor.registry_number || "",
        });
        y += 46;
      } else {
        y += 6;
      }

      // Línea separadora
      doc.setDrawColor(...COLORS.border);
      doc.setLineWidth(0.3);
      doc.line(MARGIN, y, W - MARGIN, y);
      y += 12;
    }
  }

  // ===== Plan de tratamiento =====
  if (plan.length > 0) {
    doc.addPage(); y = MARGIN;
    y = renderSectionTitle(doc, `Plan de tratamiento (${plan.length})`, y);
    const STATUS_PLAN = {
      planeado: "Planeado",
      en_proceso: "En proceso",
      completado: "Completado",
      cancelado: "Cancelado",
    };
    autoTable(doc, {
      startY: y + 4,
      head: [["Tratamiento", "Diente", "Estado", "Precio"]],
      body: plan.map((it) => [
        it.treatment_name || it.name || "—",
        it.tooth || "—",
        STATUS_PLAN[it.status] || it.status || "—",
        formatMoney(it.price || 0),
      ]),
      theme: "striped",
      headStyles: { ...brandedTableHeadStyles(branding), fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLORS.text },
      columnStyles: { 3: { halign: "right", fontStyle: "bold" } },
      margin: { left: MARGIN, right: MARGIN },
    });
    y = doc.lastAutoTable.finalY + 12;
    const totalPlan = plan.reduce((acc, it) => acc + (Number(it.price) || 0), 0);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...COLORS.text);
    doc.text(`Total plan: ${formatMoney(totalPlan)}`, W - MARGIN, y, { align: "right" });
    y += 14;
  }

  // ===== Presupuestos =====
  if (budgets.length > 0) {
    if (y > H - 120) { doc.addPage(); y = MARGIN; } else { y += 8; }
    y = renderSectionTitle(doc, `Presupuestos (${budgets.length})`, y);
    autoTable(doc, {
      startY: y + 4,
      head: [["Número", "Fecha", "Items", "Subtotal", "Total"]],
      body: budgets.map((b) => [
        b.number || "—",
        fmtDateTime(b.created_at),
        String(b.items?.length || 0),
        formatMoney(b.subtotal || 0),
        formatMoney(b.total || 0),
      ]),
      theme: "striped",
      headStyles: { ...brandedTableHeadStyles(branding), fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLORS.text },
      columnStyles: { 3: { halign: "right" }, 4: { halign: "right", fontStyle: "bold" } },
      margin: { left: MARGIN, right: MARGIN },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // ===== Recibos =====
  if (receipts.length > 0) {
    if (y > H - 120) { doc.addPage(); y = MARGIN; } else { y += 8; }
    y = renderSectionTitle(doc, `Recibos (${receipts.length})`, y);
    const SETT = { cobrado: "Cobrado", cxc: "CxC", cortesia: "Cortesía" };
    autoTable(doc, {
      startY: y + 4,
      head: [["Recibo", "Fecha", "Tipo", "Procedimiento", "Total"]],
      body: receipts.map((r) => [
        r.number || "—",
        fmtDateTime(r.created_at),
        SETT[r.settlement_type] || r.settlement_type || "—",
        (r.evolution_summary || "").slice(0, 60),
        formatMoney(r.total_charged || 0),
      ]),
      theme: "striped",
      headStyles: { ...brandedTableHeadStyles(branding), fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLORS.text },
      columnStyles: { 4: { halign: "right", fontStyle: "bold" } },
      margin: { left: MARGIN, right: MARGIN },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // Footer en la última página
  renderBrandedFooter(doc, branding);

  const safeName = (patient?.name || "paciente").replace(/\s+/g, "_");
  doc.save(`historia_clinica_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ---------------- Helpers visuales ----------------
function renderSectionTitle(doc, title, y) {
  const W = doc.internal.pageSize.getWidth();
  if (y > doc.internal.pageSize.getHeight() - 100) {
    doc.addPage(); y = MARGIN;
  }
  doc.setTextColor(...COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, MARGIN, y);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y + 4, W - MARGIN, y + 4);
  return y + 18;
}

function renderKeyValueGrid(doc, fields, y) {
  const W = doc.internal.pageSize.getWidth();
  const colW = (W - 2 * MARGIN) / 2;
  doc.setFontSize(9);
  let col = 0;
  let rowY = y;
  const visible = fields.filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "" && v !== "—");
  for (const [k, v] of visible) {
    const x = MARGIN + col * colW;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.muted);
    doc.text(k.toUpperCase(), x, rowY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.text);
    const lines = doc.splitTextToSize(String(v), colW - 8);
    doc.text(lines, x, rowY + 12);
    col += 1;
    if (col >= 2) {
      col = 0;
      rowY += 30;
      if (rowY > doc.internal.pageSize.getHeight() - 100) {
        doc.addPage(); rowY = MARGIN;
      }
    }
  }
  return rowY + (col === 0 ? 0 : 30);
}

function renderLongText(doc, label, text, y) {
  const W = doc.internal.pageSize.getWidth();
  if (y > doc.internal.pageSize.getHeight() - 80) { doc.addPage(); y = MARGIN; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);
  doc.text(label.toUpperCase(), MARGIN, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  const lines = doc.splitTextToSize(text, W - 2 * MARGIN);
  doc.text(lines, MARGIN, y + 14);
  return y + 14 + lines.length * 12 + 6;
}
